import { FC, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Svg, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { ChipsTemplate } from '@/surveys/components';
import { getSurveyCampaignsResultsStatsCall, getTemplatesCall } from '@/surveys/state';
import { getDepartmentTeamDetailsCall } from '../../../state';
import { EmptyState } from '../EmptyState';
import { EmptyStateNoCompletedEmployees } from '../EmptyStateNoCompletedEmployees';
import { AssessmentByTemplate } from './AssessmentByTemplate';

type Props = {
  departmentId?: string;
  teamId?: string;
  teamName?: string;
};

export const TabAssessmentTeam: FC<Props> = ({ departmentId, teamId, teamName }) => {
  const t = useTranslations('departments.tabs.team.assessment');
  const tShared = useTranslations();

  const [activeChip, setActiveChip] = useState<string>('');

  const { response: responseGetTemplates, success: successGetTemplates } = getTemplatesCall.hook();

  const {
    execute: executeGetSurveyCampaignsResultsStats,
    response: responseGetSurveyCampaignsResultsStats,
    error: errorGetSurveyCampaignsResultsStats,
    success: successGetSurveyCampaignsResultsStats,
    loading: loadingGetSurveyCampaignsResultsStats,
    hasMoreThanOneSurveyCampaignTemplate,
    teamCampaignsCount,
  } = getSurveyCampaignsResultsStatsCall.hook();

  const {
    execute: executeGetDepartmentTeamDetails,
    departmentTeamDetails,
    success: successGetDepartmentTeamDetails,
    loading: loadingGetDepartmentTeamDetails,
    error: errorGetDepartmentTeamDetails,
  } = getDepartmentTeamDetailsCall.hook();

  useEffect(() => {
    setActiveChip('');
    if (departmentId && teamId && successGetTemplates) {
      executeGetSurveyCampaignsResultsStats({
        departmentId,
        teamId,
        successSaga: ({ response }) => {
          if (response.length > 0) {
            const campaignWithInvited = response.filter(
              (item: { stats: { teamStats: { totalInvited: number } } }) =>
                item.stats?.teamStats &&
                item.stats.teamStats.totalInvited !== undefined &&
                item.stats.teamStats.totalInvited > 0,
            );
            if (campaignWithInvited.length > 0) {
              const firstTemplate = responseGetTemplates?.find(
                template => template.id === campaignWithInvited[0].surveyTemplateId,
              );
              if (firstTemplate && firstTemplate.name) {
                setActiveChip(firstTemplate.name);
              }
            }
          }
        },
      });
      executeGetDepartmentTeamDetails({ teamId, departmentId });
    }
  }, [
    departmentId,
    executeGetDepartmentTeamDetails,
    executeGetSurveyCampaignsResultsStats,
    responseGetTemplates,
    teamId,
    successGetTemplates,
  ]);

  if (errorGetSurveyCampaignsResultsStats && errorGetDepartmentTeamDetails) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (loadingGetSurveyCampaignsResultsStats && loadingGetDepartmentTeamDetails) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (successGetSurveyCampaignsResultsStats && successGetDepartmentTeamDetails) {
    if (
      !departmentTeamDetails?.stats?.completedEmployees ||
      departmentTeamDetails.stats.completedEmployees === 0
    ) {
      return <EmptyStateNoCompletedEmployees />;
    }

    if (responseGetSurveyCampaignsResultsStats?.length === 0 || teamCampaignsCount === 0) {
      return <EmptyState />;
    }
    return (
      <Box>
        <Box mt='m'>
          <Box display='flex' flexDirection='row' alignItems='flex-start'>
            <Typography variant='h1'>{teamName}</Typography>
            <Box ml='xs' alignItems='center' flexDirection='row' display='flex'>
              <Svg name='users' size='s' />
              <Typography ml='xxs' variant='h1'>
                {departmentTeamDetails?.stats?.totalEmployees}
              </Typography>
            </Box>
          </Box>
          {hasMoreThanOneSurveyCampaignTemplate && (
            <>
              <Typography variant='p3/light' mt='xxs' mb='m'>
                {t('description')}
              </Typography>
              <ChipsTemplate setActiveChip={setActiveChip} activeChip={activeChip} />
            </>
          )}
          {activeChip && successGetSurveyCampaignsResultsStats && (
            <AssessmentByTemplate
              activeChip={activeChip}
              departmentId={departmentId}
              teamId={teamId}
              teamName={teamName}
            />
          )}
        </Box>
      </Box>
    );
  }

  return null;
};
